import React from 'react';
import { logoUrl, contactInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Phone, Clock, Instagram } from 'lucide-react';

const Footer = () => {
  const { t } = useLanguage();

  const navigationLinks = [
    { id: 'pocetna', label: t.nav.home, href: '#pocetna' },
    { id: 'o-nama', label: t.nav.about, href: '#o-nama' },
    { id: 'usluge', label: t.nav.services, href: '#usluge' },
    { id: 'galerija', label: t.nav.gallery, href: '#galerija' },
    { id: 'tim', label: t.nav.team, href: '#tim' },
    { id: 'kontakt', label: t.nav.contact, href: '#kontakt' }
  ];

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const targetId = href.replace('#', '');
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-black pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Logo & About */}
          <div className="lg:col-span-1">
            <img
              src={logoUrl}
              alt="Studio 149 Logo"
              className="h-[70px] w-auto mb-6"
            />
            <p className="text-gray-500 text-sm leading-relaxed">
              {t.footer.description}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-serif font-bold text-lg mb-6">{t.footer.quickLinks}</h4>
            <ul className="space-y-3">
              {navigationLinks.map(link => (
                <li key={link.id}>
                  <a
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="text-gray-500 hover:text-[#0067ff] transition-colors duration-300 text-sm"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-serif font-bold text-lg mb-6">{t.footer.contactInfo}</h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin className="w-4 h-4 text-[#0067ff] mt-1 flex-shrink-0" />
                <span className="text-gray-500 text-sm">{contactInfo.address}</span>
              </li>
              <li className="flex items-start space-x-3">
                <Phone className="w-4 h-4 text-[#0067ff] mt-1 flex-shrink-0" />
                <a
                  href={`tel:${contactInfo.phone.replace(/[^0-9+]/g, '')}`}
                  className="text-gray-500 hover:text-[#0067ff] transition-colors text-sm"
                >
                  {contactInfo.phone}
                </a>
              </li>
            </ul>
            <h4 className="text-white font-serif font-bold text-lg mt-6 mb-4">Instagram</h4>
            <a
              href="https://www.instagram.com/studio149.rs/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 rounded-lg flex items-center justify-center hover:scale-110 transition-transform duration-300 inline-flex"
              data-testid="footer-instagram-link"
            >
              <Instagram className="w-5 h-5 text-white" />
            </a>
          </div>

          {/* Working Hours */}
          <div>
            <h4 className="text-white font-serif font-bold text-lg mb-6">{t.contact.hours}</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Clock className="w-4 h-4 text-[#0067ff] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-gray-500 text-sm">{t.contact.weekdays}</p>
                  <p className="text-gray-500 text-sm">{t.contact.saturday}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-center items-center">
            <p className="text-gray-600 text-sm">
              © {new Date().getFullYear()} Studio 149. {t.footer.rights}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
